import {
  PermissionCode,
  createArticleGroupRequestSchema,
  type CreateArticleGroupRequest,
} from '@gb-ticket/shared-types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiRequestError } from '@/lib/api';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { adminTableFrameClassName } from '@/platform/features/admin-panel';
import {
  useArticleGroups,
  useCreateArticleGroup,
  usePatchArticleGroup,
} from '@/platform/features/articles/use-articles';
import { MasterDataListState } from '@/platform/features/master-data/master-data-list-state';

const NONE = 'none';

function blankToNull(value: string | null): string | null {
  if (value == null) {
    return null;
  }
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

export function ArticleGroupsPage() {
  const me = useMe();
  const canWrite = hasPermission(me.data, PermissionCode.ARTICLES_MANAGE);
  const groups = useArticleGroups(true);
  const nameById = new Map(groups.data?.items.map((item) => [item.id, item.code]));

  return (
    <section className="space-y-6">
      <PlatformPageHeader
        title="Artikelgruppen"
        description="Code und Bezeichnung. Inaktive Gruppen bleiben in der Verwaltung sichtbar."
      />
      <MasterDataListState
        pending={groups.isPending}
        pendingLabel="Artikelgruppen werden geladen …"
        error={groups.isError ? groups.error : null}
        errorLabel="Die Artikelgruppen konnten nicht geladen werden."
        empty={groups.data?.items.length === 0}
        emptyLabel="Noch keine Artikelgruppe."
      >
        <div className={adminTableFrameClassName}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Bezeichnung</TableHead>
                <TableHead>Eltern</TableHead>
                <TableHead>Status</TableHead>
                {canWrite ? <TableHead>Aktion</TableHead> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {groups.data?.items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="font-mono text-sm">{item.code}</TableCell>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>
                    {item.parentId ? (nameById.get(item.parentId) ?? item.parentId) : '—'}
                  </TableCell>
                  <TableCell>
                    {item.isActive ? (
                      <Badge variant="outline">Aktiv</Badge>
                    ) : (
                      <Badge variant="secondary">inaktiv</Badge>
                    )}
                  </TableCell>
                  {canWrite ? (
                    <TableCell>{item.isActive ? <InactivateGroup id={item.id} /> : null}</TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </MasterDataListState>
      {canWrite ? <GroupForm parentOptions={groups.data?.items ?? []} /> : null}
    </section>
  );
}

function InactivateGroup({ id }: { id: string }) {
  const patch = usePatchArticleGroup(id);

  async function inactivate() {
    try {
      await patch.mutateAsync({ isActive: false });
      toast.success('Artikelgruppe inaktiviert.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Artikelgruppe konnte nicht inaktiviert werden.',
      );
    }
  }

  return (
    <Button
      type="button"
      variant="outline"
      disabled={patch.isPending}
      onClick={() => void inactivate()}
    >
      Inaktivieren
    </Button>
  );
}

function GroupForm({
  parentOptions,
}: {
  parentOptions: { id: string; code: string; name: string; isActive: boolean }[];
}) {
  const copy = useVoiceCopy();
  const create = useCreateArticleGroup();
  const form = useForm<CreateArticleGroupRequest>({
    resolver: zodResolver(createArticleGroupRequestSchema),
    defaultValues: { code: '', name: '', parentId: null },
  });

  async function onValid(values: CreateArticleGroupRequest) {
    try {
      await create.mutateAsync({
        ...values,
        parentId: values.parentId ?? null,
      });
      toast.success('Artikelgruppe angelegt.');
      form.reset({ code: '', name: '', parentId: null });
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Artikelgruppe konnte nicht angelegt werden.',
      );
    }
  }

  return (
    <form className="max-w-xl space-y-4" onSubmit={form.handleSubmit(onValid)} noValidate>
      <div className="space-y-2">
        <Label htmlFor="group-code">Code</Label>
        <Input id="group-code" {...form.register('code')} />
        {form.formState.errors.code ? (
          <p role="alert" className="text-sm text-destructive">
            {copy('platform.form.code')}
          </p>
        ) : null}
      </div>
      <div className="space-y-2">
        <Label htmlFor="group-name">Bezeichnung</Label>
        <Input id="group-name" {...form.register('name')} />
        {form.formState.errors.name ? (
          <p role="alert" className="text-sm text-destructive">
            {copy('platform.form.designation')}
          </p>
        ) : null}
      </div>
      <div className="space-y-2">
        <Label htmlFor="group-parent">Elterngruppe</Label>
        <Select
          value={form.watch('parentId') ?? NONE}
          onValueChange={(value) =>
            form.setValue('parentId', blankToNull(value === NONE ? '' : value))
          }
        >
          <SelectTrigger id="group-parent" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>Keine</SelectItem>
            {parentOptions
              .filter((group) => group.isActive)
              .map((group) => (
                <SelectItem key={group.id} value={group.id}>
                  {group.code} · {group.name}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>
      <Button type="submit" disabled={create.isPending}>
        Anlegen
      </Button>
    </form>
  );
}
